import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";
import { useAuth } from "@/context/AuthContext";
import { ShoppingCart, Network } from "lucide-react";
import { parseRemaining } from "./dashboard/data";
import { Sidebar } from "./dashboard/Sidebar";
import { HomeTab } from "./dashboard/HomeTab";
import { ShopResi } from "./dashboard/ShopResi";
import { ShopISP } from "./dashboard/ShopISP";
import { AdminPanel } from "./dashboard/AdminPanel";

const TITLES = { dashboard: "Command Center", resi: "Shop Residential", isp: "Shop ISP", subnets: "Subnets", admin: "Admin Panel" };

function SubnetsTab({ ispOrders }) {
  if (ispOrders.length === 0) {
    return <div className="bg-white border border-[#E4EEEC] rounded-3xl p-10 text-center text-zinc-400">Purchase ISP proxies to see your dedicated subnets here.</div>;
  }
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {ispOrders.map((o) => (
        <div key={o.order_id} className="led-border bg-white border border-[#E4EEEC] rounded-2xl p-5 shadow-sm" data-testid={`subnet-${o.order_id}`}>
          <div className="flex items-center gap-2 mb-2"><Network className="w-4 h-4 text-neon" aria-hidden="true" /><span className="font-mono text-sm text-ink">{o.quantity} IP subnet</span></div>
          <div className="text-xs text-zinc-400 font-mono">{o.order_id}</div>
          <div className="mt-3"><span className="text-xs font-mono text-neon border border-neon/30 rounded px-2 py-0.5">{o.status}</span></div>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [accounts, setAccounts] = useState([]);
  const [pools, setPools] = useState([]);
  const [ispOrders, setIspOrders] = useState([]);
  const [config, setConfig] = useState(null);
  const [usage, setUsage] = useState(null);

  const load = useCallback(async () => {
    try {
      const [a, p, c, o, u] = await Promise.all([api.get("/proxy-accounts"), api.get("/pools"), api.get("/config"), api.get("/isp-orders"), api.get("/usage/summary?days=7")]);
      setAccounts(a.data); setPools(Array.isArray(p.data) ? p.data : (p.data.pools || [])); setConfig(c.data); setIspOrders(o.data); setUsage(u.data);
    } catch (e) { logError("Dashboard load failed", e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const totalRemaining = useMemo(() => accounts.reduce((s, a) => s + (Number(parseRemaining(a.remote)) || 0), 0), [accounts]);
  const usedGb = usage?.total_gb ?? 0;

  return (
    <div className="min-h-screen" style={{ background: "transparent" }}>
      <Sidebar tab={tab} setTab={setTab} user={user} logout={logout} />
      <main className="ml-[84px] px-6 lg:px-10 py-7 max-w-[1400px] fade-up">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <div className="text-[11px] font-mono tracking-[0.25em] text-neon uppercase">{TITLES[tab]}</div>
            <h1 className="font-head text-2xl font-extrabold text-ink">Hi {(user?.name || user?.email || "operator").split("@")[0]} 👋</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-zinc-600 bg-white border border-[#E4EEEC] rounded-full px-4 py-2 shadow-sm" data-testid="status-pill">
              <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-neon opacity-60" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-neon" /></span>
              Operational
            </div>
            <button onClick={() => setTab("resi")} data-testid="cart-button" className="w-11 h-11 grid place-items-center rounded-full border border-[#E4EEEC] bg-white hover:border-neon transition-colors relative">
              <ShoppingCart className="w-4 h-4 text-zinc-600" aria-hidden="true" />
              {accounts.length > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-neon text-white text-[10px] grid place-items-center">{accounts.length}</span>}
            </button>
          </div>
        </div>

        {tab === "dashboard" && (
          <HomeTab user={user} load={load} totalRemaining={totalRemaining} usedGb={usedGb} usage={usage}
            accounts={accounts} ispOrders={ispOrders} pools={pools} config={config} setTab={setTab} />
        )}
        {tab === "resi" && <ShopResi accounts={accounts} />}
        {tab === "isp" && <ShopISP ispOrders={ispOrders} />}
        {tab === "admin" && user?.role === "admin" && <AdminPanel />}
        {tab === "subnets" && <SubnetsTab ispOrders={ispOrders} />}
      </main>
    </div>
  );
}
